/**
 * Food hygiene rating check (snippets/cfs-hygiene-rating.liquid).
 *
 * Liquid draws the badge from Theme settings. The Food Standards Agency only allows the CURRENT
 * rating to be shown, so when the badge nears the screen this asks the FSA's public register (CORS
 * open, no key, no cookies) and corrects the badge if the rating or inspection date has changed.
 * One request per session (kept 12 hours in sessionStorage); silent if it fails or is blocked,
 * in which case the badge stays as set in Theme settings.
 */
const CACHE_KEY = 'cfs:fhrs';
const MAX_AGE = 12 * 60 * 60 * 1000;
const TEXT = {
  0: '0: Urgent improvement necessary',
  1: '1: Major improvement necessary',
  2: '2: Improvement necessary',
  3: '3: Generally satisfactory',
  4: '4: Good',
  5: '5: Very good',
  AwaitingInspection: 'awaiting inspection',
  AwaitingPublication: 'awaiting publication',
  Exempt: 'exempt',
};

class CfsHygiene extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    if (!('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.#observer?.disconnect();
        this.#check();
      },
      { rootMargin: '300px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  async #check() {
    const id = this.dataset.fhrsId;
    const record = id ? await lookup(id) : null;
    const rating = String(record?.RatingValue ?? '').replaceAll(' ', '');
    if (!record || !Object.hasOwn(TEXT, rating)) return;

    const numeric = rating.length === 1;
    const date = new Date(record.RatingDate);
    const dateText =
      numeric && date.getFullYear() > 2000
        ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
        : '';
    const changed = rating !== this.dataset.rating;

    if (changed) {
      const img = this.querySelector('img');
      if (!img) return;
      img.src = rating === '5' && this.dataset.localSrc ? this.dataset.localSrc : `${this.dataset.imageBase}${rating}.svg`;
      img.alt = `Food hygiene rating is ${TEXT[rating]}`;
      img.height = !numeric ? 104 : Number(rating) < 4 ? 163 : 162; // the badge files' own proportions
      this.dataset.rating = rating;
    }

    // The caption is typed in Theme settings: rewrite it when the rating changed or it names another date.
    const note = this.querySelector('.cfs-hygiene__note');
    if (note && (changed || (dateText && !note.textContent?.includes(dateText)))) {
      note.textContent = dateText ? `Inspected on ${dateText}` : '';
    }
  }
}

/** @param {string} id */
async function lookup(id) {
  try {
    const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) ?? 'null');
    if (cached?.id === id && Date.now() - cached.at < MAX_AGE) return cached.record;
  } catch {
    // Storage blocked or unreadable: ask the register instead.
  }
  try {
    const response = await fetch(`https://api.ratings.food.gov.uk/Establishments/${encodeURIComponent(id)}`, {
      headers: { 'x-api-version': '2', accept: 'application/json' },
      signal: AbortSignal.timeout(5000),
    });
    if (!response.ok) return null;
    const { RatingValue, RatingDate } = await response.json();
    const record = { RatingValue, RatingDate };
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ id, at: Date.now(), record }));
    } catch {
      // Private mode: no cache, the next page asks again.
    }
    return record;
  } catch {
    return null;
  }
}

if (!customElements.get('cfs-hygiene')) customElements.define('cfs-hygiene', CfsHygiene);
